/* eslint-disable */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Wand2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Gift,
  Coins,
  Filter,
  ChevronLeft,
  ChevronRight,
  Package,
  Link2,
  Calendar,
} from "lucide-react";
import { fetchAiGenerations } from "../../../api/aiGenerations";

const PAGE_SIZE = 20;

const FEATURE_FILTERS = [
  { value: "", labelKey: "foorAi.history.filters.all", fallback: "الكل" },
  {
    value: "landing_page",
    labelKey: "foorAi.history.filters.landing",
    fallback: "لاندينغ بايج",
  },
  {
    value: "product_page",
    labelKey: "foorAi.history.filters.product",
    fallback: "صفحة منتج",
  },
];

const STATUS_FILTERS = [
  {
    value: "",
    labelKey: "foorAi.history.filters.allStatus",
    fallback: "كل الحالات",
  },
  {
    value: "SUCCESS",
    labelKey: "foorAi.history.filters.success",
    fallback: "ناجح",
  },
  {
    value: "FAILED_CHECK",
    labelKey: "foorAi.history.filters.failedCheck",
    fallback: "فشل التحقق",
  },
  {
    value: "FAILED_DEBIT",
    labelKey: "foorAi.history.filters.failedDebit",
    fallback: "فشل الخصم",
  },
];

function resolveFeature(item) {
  return item?.feature || "product_page";
}

function formatDate(iso, locale) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return String(iso);
  }
}

function StatusBadge({ status, usedFree, t }) {
  if (status === "SUCCESS" && usedFree) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 text-xs font-semibold dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
        <Gift size={13} />
        {t("foorAi.history.status.free")}
      </span>
    );
  }
  if (status === "SUCCESS") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 text-xs font-semibold dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
        <CheckCircle2 size={13} />
        {t("foorAi.history.status.success")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-[#9b59bf] border border-purple-100 px-2.5 py-1 text-xs font-semibold dark:bg-purple-900/30 dark:text-[#c88ef2] dark:border-purple-800">
      <XCircle size={13} />
      {status === "FAILED_CHECK"
        ? t("foorAi.history.status.failedCheck")
        : t("foorAi.history.status.failedDebit")}
    </span>
  );
}

function FeatureIcon({ feature }) {
  if (feature === "landing_page") {
    return (
      <div className="w-11 h-11 rounded-xl bg-purple-100 text-[#9b59bf] flex items-center justify-center dark:bg-[#2a2a2c] dark:text-[#c88ef2]">
        <Wand2 size={20} />
      </div>
    );
  }
  return (
    <div className="w-11 h-11 rounded-xl bg-violet-100 text-[#7c3aed] flex items-center justify-center dark:bg-[#2a2a2c] dark:text-[#a78bfa]">
      <Sparkles size={20} />
    </div>
  );
}

function CostPill({ item, t }) {
  if (item.usedFree && item.status === "SUCCESS") {
    return (
      <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
        {t("foorAi.history.status.free")}
      </span>
    );
  }
  const amount = item.chargedTokens ?? 0;
  return (
    <span className="text-base font-bold tabular-nums text-[#9b59bf] dark:text-[#c88ef2]">
      {amount > 0 ? `−${amount}` : "0"}
    </span>
  );
}

function SummaryCards({ itemsOnPage, total, t }) {
  const successOnPage = itemsOnPage.filter((i) => i.status === "SUCCESS");
  const tokensSpentOnPage = successOnPage.reduce(
    (s, i) => s + (i.chargedTokens || 0),
    0,
  );
  const freeOnPage = successOnPage.filter((i) => i.usedFree).length;
  const failedOnPage = itemsOnPage.filter((i) => i.status !== "SUCCESS").length;

  const cards = [
    {
      label: t("foorAi.history.summary.total"),
      value: total,
      icon: Package,
      tone: "purple",
    },
    {
      label: t("foorAi.history.summary.tokensPage"),
      value: tokensSpentOnPage,
      icon: Coins,
      tone: "violet",
    },
    {
      label: t("foorAi.history.status.free"),
      value: freeOnPage,
      icon: Gift,
      tone: "emerald",
    },
    {
      label: t("foorAi.history.summary.failedPage"),
      value: failedOnPage,
      icon: AlertTriangle,
      tone: "purple",
    },
  ];

  const toneMap = {
    purple:
      "bg-purple-50 text-[#9b59bf] border-purple-100 dark:bg-[#2a2a2c] dark:border-[#3c3a3d] dark:text-[#c88ef2]",
    violet:
      "bg-violet-50 text-[#7c3aed] border-violet-100 dark:bg-[#2a2a2c] dark:border-[#3c3a3d] dark:text-[#a78bfa]",
    emerald:
      "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-[#2a2a2c] dark:border-[#3c3a3d] dark:text-emerald-300",
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.label}
            className={`rounded-xl border p-3.5 flex items-center gap-3 ${toneMap[c.tone]}`}
          >
            <div className="w-10 h-10 rounded-lg bg-white/70 dark:bg-[#181819] flex items-center justify-center shrink-0">
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-xs opacity-80 truncate">{c.label}</div>
              <div className="text-xl font-bold tabular-nums">{c.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryRow({ item, t, i18n }) {
  const feature = resolveFeature(item);
  const failed = item.status !== "SUCCESS";

  return (
    <div
      className={`flex items-start gap-3 p-4 border-b last:border-b-0 border-purple-50 dark:border-[#3c3a3d] transition-colors ${
        failed
          ? "bg-purple-50/50 hover:bg-purple-50 dark:bg-purple-950/20 dark:hover:bg-purple-950/30"
          : "bg-white hover:bg-purple-50/40 dark:!bg-[#181819] dark:hover:!bg-[#1f1f21]"
      }`}
    >
      <FeatureIcon feature={feature} />

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {feature === "landing_page"
              ? t("foorAi.history.feature.landing")
              : t("foorAi.history.feature.product")}
          </span>
          <StatusBadge status={item.status} usedFree={item.usedFree} t={t} />
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
          {(item.productSku || item.productUrl) && (
            <span className="inline-flex items-center gap-1 max-w-full truncate">
              <Link2 size={14} className="shrink-0" />
              <span className="truncate">
                {item.productSku || item.productUrl}
              </span>
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Calendar size={14} />
            {formatDate(
              item.createdAt,
              i18n.language === "ar"
                ? "ar-DZ"
                : i18n.language === "fr"
                  ? "fr-FR"
                  : "en-US",
            )}
          </span>
          {item.plan ? (
            <span className="rounded-md bg-gray-100 dark:bg-[#2a2a2c] px-2 py-0.5 text-xs font-medium">
              {item.plan}
            </span>
          ) : null}
        </div>

        {failed && item.error && (
          <p className="mt-1.5 text-sm text-[#9b59bf] dark:text-[#c88ef2] font-medium">
            {item.error === "INSUFFICIENT_TOKENS"
              ? t("foorAi.history.error.insufficient")
              : t("foorAi.history.error.generic")}
          </p>
        )}

        {item.status === "SUCCESS" && (
          <p className="mt-1 text-xs text-gray-400 tabular-nums">
            {item.tokensBefore ?? "—"} → {item.tokensAfter ?? "—"}{" "}
            {t("foorAi.history.tokens")}
          </p>
        )}
      </div>

      <div className="shrink-0 text-end pt-0.5">
        <CostPill item={item} t={t} />
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="divide-y divide-purple-50 dark:divide-[#3c3a3d]">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-4 bg-[#fbf8fc] dark:!bg-[#181819]"
        >
          <div className="w-11 h-11 rounded-xl bg-[#efe6f5] dark:bg-[#2a2a2c]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-36 rounded bg-[#efe6f5] dark:bg-[#2a2a2c]" />
            <div className="h-3 w-52 rounded bg-[#f5eef9] dark:bg-[#222]" />
          </div>
          <div className="h-4 w-12 rounded bg-[#efe6f5] dark:bg-[#2a2a2c]" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-[#2a2a2c] flex items-center justify-center mb-4">
        <Sparkles className="w-8 h-8 text-[#9e68ba]" />
      </div>
      <h3 className="text-lg font-bold text-[#9e68ba] mb-1">
        {t("foorAi.history.empty.title")}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
        {t("foorAi.history.empty.desc")}
      </p>
    </div>
  );
}

function SummaryCardsSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-purple-100 dark:border-[#3c3a3d] p-3.5 flex items-center gap-3 bg-[#fbf8fc] dark:!bg-[#181819]"
        >
          <div className="w-10 h-10 rounded-lg bg-[#efe6f5] dark:bg-[#2a2a2c] shrink-0" />
          <div className="flex-1 space-y-2 min-w-0">
            <div className="h-3 w-20 rounded bg-[#efe6f5] dark:bg-[#2a2a2c]" />
            <div className="h-5 w-12 rounded bg-[#f5eef9] dark:bg-[#222]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AiGenerationHistory() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl" || i18n.language === "ar";

  const [feature, setFeature] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: PAGE_SIZE,
    totalPages: 1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchAiGenerations({
        feature: feature || undefined,
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setData({
        items: res.items || [],
        total: res.total ?? 0,
        page: res.page ?? page,
        limit: res.limit ?? PAGE_SIZE,
        totalPages: res.totalPages ?? 1,
      });
    } catch (err) {
      console.error("AI generations history:", err);
      setError(t("foorAi.history.loadError"));
      setData((prev) => ({ ...prev, items: [], total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [feature, status, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [feature, status]);

  const canPrev = page > 1;
  const canNext = page < data.totalPages;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 dark:!bg-[#181819] dark:!border-[#3c3a3d] overflow-hidden">
      <div className="p-4 border-b border-purple-50 dark:border-[#3c3a3d] space-y-3">
        <div className="flex items-center gap-2 text-base font-semibold text-[#9e68ba] dark:text-[#c88ef2]">
          <Filter size={18} />
          {t("foorAi.history.filters.title")}
        </div>

        <div className="flex flex-wrap gap-2">
          {FEATURE_FILTERS.map((f) => {
            const active = feature === f.value;
            return (
              <button
                key={f.value || "all"}
                type="button"
                onClick={() => setFeature(f.value)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#9b59bf] text-white shadow-sm"
                    : "bg-purple-50 text-[#9e68ba] hover:bg-purple-100 dark:bg-[#2a2a2c] dark:text-[#c88ef2] dark:hover:bg-[#333]"
                }`}
              >
                {t(f.labelKey, { defaultValue: f.fallback })}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => {
            const active = status === f.value;
            return (
              <button
                key={f.value || "all-status"}
                type="button"
                onClick={() => setStatus(f.value)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#9b59bf] text-white shadow-sm"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-[#2a2a2c] dark:text-gray-300 dark:hover:bg-[#333]"
                }`}
              >
                {t(f.labelKey, { defaultValue: f.fallback })}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <SummaryCardsSkeleton />
        ) : !error ? (
          <SummaryCards itemsOnPage={data.items} total={data.total} t={t} />
        ) : null}

        {error && (
          <div className="mb-4 rounded-xl border border-purple-100 bg-purple-50 text-[#9b59bf] text-sm p-3 dark:bg-purple-950/30 dark:border-purple-900 dark:text-[#c88ef2]">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-purple-100 dark:border-[#3c3a3d] overflow-hidden">
          {loading ? (
            <SkeletonList />
          ) : data.items.length === 0 ? (
            <EmptyState t={t} />
          ) : (
            data.items.map((item) => (
              <HistoryRow key={item._id} item={item} t={t} i18n={i18n} />
            ))
          )}
        </div>

        {!loading && data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("foorAi.history.pagination", {
                page: data.page,
                total: data.totalPages,
              })}
              {" · "}
              {data.total} {t("foorAi.history.records")}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-9 w-9 rounded-lg border border-purple-100 dark:border-[#3c3a3d] flex items-center justify-center disabled:opacity-40 hover:bg-purple-50 dark:hover:bg-[#2a2a2c] transition-colors"
              >
                {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
              <button
                type="button"
                disabled={!canNext}
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                className="h-9 w-9 rounded-lg border border-purple-100 dark:border-[#3c3a3d] flex items-center justify-center disabled:opacity-40 hover:bg-purple-50 dark:hover:bg-[#2a2a2c] transition-colors"
              >
                {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
